import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Button,
  Autocomplete,
  TextField,
  Link as MuiLink,
  InputAdornment,
  Chip,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import Header from '../Header';
import Footer from '../Footer';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import { API_URL } from '../Config';
import config from "../common/Config";

const BatchWiseStockReport = () => {
  const [reportrange, setReportrange] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [salescategory, setSalescategory] = useState('');
  const [salesbrand, setSalesbrand] = useState('');
  const [data, setData] = useState([]);
  const [footerTotals, setFooterTotals] = useState({
    totalBatch: '0',
    totalBoxQty: '0',
    totalPiecesQty: '0'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryInputValue, setCategoryInputValue] = useState('');
  const [filteredCategoryOptions, setFilteredCategoryOptions] = useState([]);
  
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandInputValue, setBrandInputValue] = useState('');
  const [filteredBrandOptions, setFilteredBrandOptions] = useState([]);
  
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');

  const handleDateChange = (value) => {
    setDates(value);
    const formattedStartDate = value[0] ? format(value[0], 'dd/MM/yyyy') : '';
    const formattedEndDate = value[1] ? format(value[1], 'dd/MM/yyyy') : '';
    setReportrange(formattedStartDate ? `${formattedStartDate} - ${formattedEndDate}` : '');
    setPage(0);
  };

  const fetchCategoriesOptions = async () => {
    try {
      const res = await fetch(`${API_URL}/stock_take/getCategories`, {
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` }
      });
      const data = await res.json();
      const formattedOptions = data.data.category.map(item => ({ value: item.id, label: item.name }));
      setCategoryOptions(formattedOptions);
      setFilteredCategoryOptions(formattedOptions);
    } catch (err) {
    }
  };

  const fetchBrandOptions = async () => {
    try {
      const res = await fetch(`${API_URL}/stock_take/getBrands`, {
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` }
      });
      const data = await res.json();
      const formattedOptions = data.data.brands.map(item => ({ value: item.id, label: item.name }));
      setBrandOptions(formattedOptions);
      setFilteredBrandOptions(formattedOptions);
    } catch (err) {
    }
  };

  const fetchReport = useCallback(async () => {
    if (!reportrange) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('draw', String(page + 1));
      formData.append('start', String(page * rowsPerPage));
      formData.append('length', String(rowsPerPage));
      formData.append('searchValue', searchValue);
      formData.append('reportrange', reportrange);
      formData.append('brand_id', salesbrand);
      formData.append('category_id', salescategory);

      const response = await fetch(`${config.API_URL}/batchWiseStockReport`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const json = await response.json();

      if (json && Array.isArray(json.data)) {
        setData(json.data);
        setTotalCount(json.recordsTotal || json.recordsFiltered || json.data.length);
        
        setFooterTotals({
          totalBatch: parseFloat(json.total_batches || 0).toFixed(0),
          totalBoxQty: parseFloat(json.total_box_qty || 0).toFixed(0),
          totalPiecesQty: parseFloat(json.total_piece_qty || 0).toFixed(0)
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, searchValue, salesbrand, salescategory]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchCategoriesOptions();
    fetchBrandOptions();
  }, [accessToken]);

  useEffect(() => {
    if (reportrange) {
      const timer = setTimeout(() => {
        fetchReport();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [fetchReport]);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setPage(0);
      fetchReport();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCategoryInputChange = (event, newInputValue) => {
    setCategoryInputValue(newInputValue);
    const filtered = categoryOptions.filter(option =>
      option.label.toLowerCase().includes(newInputValue.toLowerCase())
    );
    setFilteredCategoryOptions(filtered);
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    setSalescategory(newValue ? newValue.value : '');
  };

  const handleBrandInputChange = (event, newInputValue) => {
    setBrandInputValue(newInputValue);
    const filtered = brandOptions.filter(option =>
      option.label.toLowerCase().includes(newInputValue.toLowerCase())
    );
    setFilteredBrandOptions(filtered);
  };

  const handleBrandChange = (event, newValue) => {
    setSelectedBrand(newValue);
    setSalesbrand(newValue ? newValue.value : '');
  };

  const handleSubmit = () => {
    setPage(0);
    fetchReport();
  };

  return (
    <>
      {/* <Header /> */}
      <Box className="page-container">
          <Paper className="paper-style">
            <Box sx={{ px: 2, py: 1.2 }}>
              <Typography className="page-title" mb={1}>
                Batch Wise Stock Report
              </Typography>

              <Grid2 container spacing={1}>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }} alignContent="end">
                <TextField
                fullWidth
                size="small"
                className="input-border"
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleSearchKeyDown}
             
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: '#0e0f0fef' }} />
                    </InputAdornment>
                  ),
                }}
              />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <CommonDatePicker
                    className="input-border"
                    value={dates}
                    showOneCalendar
                    onChange={handleDateChange}
                    format="dd/MM/yyyy"
                    character=" - "
                    style={{ width: '100%' }}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <Autocomplete
                    className="input-border"
                    size="small"
                    options={filteredCategoryOptions}
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    inputValue={categoryInputValue}
                    onInputChange={handleCategoryInputChange}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Category"
                      label="Category"
                      className="text-field input-border"
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md:2}}>
                  <Autocomplete
                    className="input-border"
                    size="small"
                    options={filteredBrandOptions}
                    value={selectedBrand}
                    onChange={handleBrandChange}
                    inputValue={brandInputValue}
                    onInputChange={handleBrandInputChange}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Brand"
                      label="Brand"
                      className="text-field input-border"
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md:1}} alignContent="end">
                  <Stack direction="row" spacing={1}>
                    <Button
                      className="button-variant-contained"
                      variant="contained"
                      onClick={handleSubmit}
                      fullWidth
                    >
                      Submit
                    </Button>
                  </Stack>
                </Grid2>
              </Grid2>
            </Box>

            <TableContainer >
              <Table size="small" layout="fixed" className="table-cell-border">
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell sx={{width: 50 }}>Sr. No.</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell sx={{ width: 300 }}>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell align="right">Total Batch</TableCell>
                    <TableCell align="right">Total Box Qty</TableCell>
                    <TableCell align="right">Total Piece Qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    data.map((row, index) => (
                      <TableRow key={index} hover className="table-cell-style">
                        <TableCell sx={{ textAlign: 'center' }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell>
                          <MuiLink
                            href={`${config.BASE_URL}/reports/batchWise_stock_details_report/${row.product_id}`}
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'underline',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {row.product_code || ''}
                          </MuiLink>
                        </TableCell>
                        <TableCell>{row.product_name || ''}</TableCell>
                        <TableCell>{row.categories || ''}</TableCell>
                        <TableCell>{row.brand || ''}</TableCell>
                        <TableCell align="right">{row.total_batches || 0}</TableCell>
                        <TableCell align="right">{row.total_box_qty || 0}</TableCell>
                        <TableCell align="right">{row.total_piece_qty || 0}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#637381', fontSize: '0.75rem' }}>
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow
                  className="table-cell-style"
                  sx={{
                    backgroundColor: '#f5f7fa',
                    '& .MuiTableCell-root': {
                      fontWeight: 600                
                    }
                  }}
                  >
                    <TableCell colSpan={5} align="right">
                      Total:
                    </TableCell>
                    <TableCell align="right">
                      {footerTotals.totalBatch}
                    </TableCell>
                    <TableCell align="right">
                      {footerTotals.totalBoxQty}
                    </TableCell>
                    <TableCell align="right">
                      {footerTotals.totalPiecesQty}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              className="pagination-styles"
            />
          </Paper>
        </Box>
    </>
  );
};

export default BatchWiseStockReport;
